/**
 * Utility function to send payment link notification webhook
 */

/** Payment link details passed in by the UI when a notification should be sent. */
export interface PaymentData {
  paymentUrl: string;
  requestId: string;
  amount: number;
  currency: string;
  customerEmail: string;
  customerName: string;
  description?: string;
  locale?: string;
}

/** Body POSTed to the notification webhook: the payment data with a resolved locale and timestamp. */
export type PaymentWebhookPayload = PaymentData & {
  locale: string;
  createdAt: string;
};

/**
 * Builds the webhook payload from payment data
 */
export function buildWebhookPayload(paymentData: PaymentData): PaymentWebhookPayload {
  return {
    paymentUrl: paymentData.paymentUrl,
    requestId: paymentData.requestId,
    amount: paymentData.amount,
    currency: paymentData.currency,
    customerEmail: paymentData.customerEmail,
    customerName: paymentData.customerName,
    description: paymentData.description,
    locale: paymentData.locale || 'en',
    createdAt: new Date().toISOString(),
  };
}

/**
 * Sample webhook payload for testing in Postman:
 * 
 * {
 *   "paymentUrl": "https://payment.example.com/pay/abc123xyz",
 *   "requestId": "BOOKING-1234",
 *   "amount": 150.50,
 *   "currency": "EUR",
 *   "customerEmail": "john.doe@example.com",
 *   "customerName": "John Doe",
 *   "description": "2 bedroom apartment booking",
 *   "locale": "en",
 *   "createdAt": "2026-01-29T12:34:56.789Z"
 * }
 */

/**
 * Sends a notification webhook with payment link information
 * @param webhookUrl - The webhook URL to send the notification to
 * @param paymentData - The payment data to include in the webhook payload
 * @returns Promise that resolves if successful, rejects on error
 */
export async function sendPaymentNotificationWebhook(
  webhookUrl: string,
  paymentData: PaymentData
): Promise<void> {
  const payload = buildWebhookPayload(paymentData);

  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Webhook returned status ${response.status}`);
  }
}
